import { format, formatDistanceToNow } from 'date-fns'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Delete, Info } from '@mui/icons-material'
import { Box, Button, Chip, IconButton, Stack, TableContainer, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { packageTierLabelKeys, packageTypeLabelKeys, sortRoleNames } from '../../../plans/components/helpers/planHelpers'
import { FormPackage } from '../helpers/organizationHelpers'

type CustomProps = {
  disabled?: boolean
  shownRoles: string[]
  onShowRoles: (packageId: string) => void
  onRemove: (planPackage: FormPackage) => void
}

type OrganizationPackagesTableProps = {
  packages: FormPackage[]
  onRemove: (planPackage: FormPackage) => void
  disabled?: boolean
}

export const formatPackageDate = (timestamp?: number) =>
  timestamp ? `${format(timestamp, 'dd.MM.yyyy HH:mm')} (${formatDistanceToNow(timestamp, { addSuffix: true })})` : undefined

export const OrganizationPackagesTable: FC<OrganizationPackagesTableProps> = ({ packages, onRemove, disabled }) => {
  const { t } = useTranslation()
  const [shownRoles, setShownRoles] = useState<string[]>([])

  const tableColumns: GRTableColumn<FormPackage, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:organizations_package'),
        accessor: ({ row }) => (
          <Stack spacing={0.5}>
            <Typography fontWeight="bold">{row.name}</Typography>
            <Typography variant="body2">
              {t(packageTypeLabelKeys[row.type] ?? '–')} / {t(packageTierLabelKeys[row.tier] ?? '–')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{t('internal-csm:organizations_monthly_price', { price: row.price })}</Typography>
              {row.note && (
                <Tooltip title={row.note} describeChild>
                  <IconButton size="small" color="primary" aria-label={t('internal-csm:organizations_sales_note')} sx={{ p: 0.25 }}>
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-csm:plans_roles'),
        accessor: ({ row, customTableProps }) =>
          customTableProps?.shownRoles.includes(row.packageId) ? (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {sortRoleNames(row.roles).map((role) => (
                <Chip key={role} label={role} size="small" />
              ))}
            </Box>
          ) : (
            <Button size="small" variant="outlined" onClick={() => customTableProps?.onShowRoles(row.packageId)}>
              {t('internal-csm:organizations_show')}
            </Button>
          ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_purchased'),
        accessor: ({ row }) => formatPackageDate(row.purchasedAt) ?? '-',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-csm:organizations_expiration'),
        accessor: ({ row }) => formatPackageDate(row.expiration) ?? t('internal-csm:organizations_continuous'),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Tooltip title={t('internal-csm:organizations_remove_package')}>
            <span>
              <IconButton
                aria-label={t('internal-csm:organizations_remove_package')}
                color="error"
                disabled={customTableProps?.disabled}
                onClick={() => customTableProps?.onRemove(row)}
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { minWidth: 70 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({ disabled, shownRoles, onShowRoles: (packageId) => setShownRoles((current) => [...current, packageId]), onRemove }),
    [disabled, shownRoles, onRemove]
  )

  return (
    <TableContainer>
      <GRTable
        rows={packages}
        columns={columns}
        onColumnsUpdated={setColumns}
        rowIdKey={(row, index) => `${row.packageId}-${index}`}
        customProps={customProps}
        noRowsLabel={t('internal-csm:organizations_no_packages')}
        striped
      />
    </TableContainer>
  )
}
